import React, { useMemo } from "react";
import ChampionsMappingWrapper from "@/components/layouts/ChampionsMapping/ChampionsMappingWrapper";
import ConversionRatesWrapper from "@/components/layouts/ConversionRates/ConversionRatesWrapper";
import KPIBar from "@/components/layouts/KPIBar";
import KPIBarV2 from "@/components/layouts/KPIBarV2/KPIBarV2";
import LeadAnalysisWrapper from "@/components/layouts/LeadAnalysis/LeadAnalysisWrapper";
import QuotaAchievementWrapper from "@/components/layouts/QuotaAchievement/QuotaAchievementWrapper";
import SalesPipelineWrapper from "@/components/layouts/SalesPipeline/SalesPipelineWrapper";
import SalesVSQuotaWrapper from "@/components/layouts/SalesVSQuota/SalesVSQuotaWrapper";
import StageAverageDurationWrapper from "@/components/layouts/StageAverageDuration/StageAverageDurationWrapper";
import StakeholdersMappingWrapper from "@/components/layouts/StakeholdersMapping/StakeholdersMappingWrapper";
import WinRateWrapper from "@/components/layouts/WinRate/WinRateWrapper";
import { UserData } from "@/entities/users/types";
import { useMyRoleType } from "@/entities/users/hooks";
import { FeatureFlags, UserRoles } from "@/services/features/types";
import { useFeatureFlag } from "@/services/features/hook";
import { useReport } from "@/entities/reporting/hooks";
import { KPIsReportData, ReportType } from "@/entities/reporting/types";
import { transformKPIResponse } from "@/entities/reporting/helpers";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useTranslation } from "react-i18next";
import { getIsCompactWidget } from "@/components/layouts/SalesVSQuota/SalesVSQuota";
import PipelineOverTimeWrapper from "@/components/layouts/PipelineOverTime/PipelineOverTimeWrapper";

interface Props {
  userData?: UserData;
  isAE: boolean;
}

const PerformanceTab: React.FC<Props> = ({ userData, isAE }) => {
  const { t } = useTranslation();
  const { label: arrMrrLabel } = useArrMrr();
  const { roleType } = useMyRoleType();

  // Show the Sales vs Quota widget if the user is an AE with a quota plan or if the user is a type of Sales Leader
  // Get SalesVSQuota data to determine widget type
  const { report: salesVsQuotaData } = useReport(ReportType.SALES_VS_QUOTA);
  const isSalesVsQuotaCompact = useMemo(() => {
    return getIsCompactWidget(salesVsQuotaData);
  }, [salesVsQuotaData]);

  const showSalesVSQuota =
    (isAE && !!userData?.quotaPlan) ||
    [
      UserRoles.ACCOUNT_EXECUTIVE_LEADER,
      UserRoles.HEAD_OF_SALES_ORGANIZATION,
      UserRoles.SALES_ADMIN
    ].includes(roleType ?? -1);

  const { report: kpisData, isLoading: isLoadingKPIs } = useReport(
    ReportType.KPIS
  );
  const transformedKPIsData = useMemo(() => {
    if (!kpisData) return [];
    return transformKPIResponse(t, kpisData, arrMrrLabel);
  }, [kpisData, arrMrrLabel, t]);
  const { enabled: isKPIBarV2Enabled, loading: isLoadingKPIBarV2Flag } =
    useFeatureFlag(FeatureFlags.CRM_9987_KPI_BAR_V2);

  return (
    <div>
      {isKPIBarV2Enabled ? (
        <KPIBarV2
          kpis={kpisData?.kpis as KPIsReportData | undefined}
          isLoading={isLoadingKPIs}
          arrMrrLabel={arrMrrLabel}
          className="p-6 pb-0"
        />
      ) : (
        <KPIBar
          kpis={transformedKPIsData}
          isLoading={isLoadingKPIs || isLoadingKPIBarV2Flag}
          className="p-6 pb-0"
        />
      )}
      <div className="flex flex-row justify-center">
        <div className="grow-1" />
        <div className="px-6 pt-6 grow-[2] max-w-[1700px]">
          <SalesPipelineWrapper userData={userData} isAE={isAE} />
        </div>
        <div className="grow-1" />
      </div>
      <div className="flex flex-row justify-center">
        <div className="grow-1" />
        <div className="px-6 pt-6 grow-[2] max-w-[1700px]">
          <PipelineOverTimeWrapper />
        </div>
        <div className="grow-1" />
      </div>
      <div className="flex flex-row justify-center">
        <div className="grow-1" />
        <div className="px-6 pt-6 grow-[2] max-w-[1700px] w-full">
          <StageAverageDurationWrapper userData={userData} isAE={isAE} />
        </div>
        <div className="grow-1" />
      </div>
      <div className="flex flex-row justify-center">
        <div className="grow-1" />
        <div className="px-6 pt-6 grow-[2] max-w-[1700px] w-full">
          <ConversionRatesWrapper userData={userData} isAE={isAE} />
        </div>
        <div className="grow-1" />
      </div>
      <div className="flex flex-row justify-center">
        <div className="grow-1" />
        <div className="px-6 pt-6 grow-[2] max-w-[1700px] w-full">
          <WinRateWrapper userData={userData} isAE={isAE} />
        </div>
        <div className="grow-1" />
      </div>
      {showSalesVSQuota && !isSalesVsQuotaCompact && (
        <div className="flex flex-row justify-center">
          <div className="grow-1" />
          <div className="px-6 pt-6 grow-[2] max-w-[1700px] w-full">
            <SalesVSQuotaWrapper
              userData={userData}
              showWidget={showSalesVSQuota}
            />
          </div>
          <div className="grow-1" />
        </div>
      )}
      <div className="flex flex-row justify-center">
        <div className="grow-1" />
        <div className="px-6 pt-6 grow-[2] max-w-[1700px] w-full">
          <LeadAnalysisWrapper userData={userData} isAE={isAE} />
        </div>
        <div className="grow-1" />
      </div>
      <div className="flex flex-col justify-center items-center w-full">
        <div className="grow-1" />
        {!isAE ? (
          <div className="p-6 gap-y-6 flex flex-col justify-around items-stretch grow-[2] max-w-[1700px] w-full">
            <div className="flex flex-row gap-x-6">
              <div className="w-1/2 min-w-[512px] max-w-[850px] ">
                <StakeholdersMappingWrapper leadUser={userData} />
              </div>
              <div className="w-1/2 min-w-[512px] max-w-[850px] ">
                <ChampionsMappingWrapper leadUser={userData} />
              </div>
            </div>
            <div className="flex flex-row gap-x-6">
              <div className="w-1/2 min-w-[512px] max-w-[850px]">
                <QuotaAchievementWrapper leadUser={userData} />
              </div>
              <div className="w-1/2 min-w-[512px] max-w-[850px]">
                {showSalesVSQuota && isSalesVsQuotaCompact && (
                  <SalesVSQuotaWrapper
                    userData={userData}
                    showWidget={showSalesVSQuota}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="pb-4 h-0 w-full"></div>
        )}
        <div className="grow-1" />
      </div>
    </div>
  );
};

export default PerformanceTab;
